<template>
    <div>
        <v-card class="clients" outlined>
            <v-card-title class="page-heading primary">
                <v-row align="center">
                    <v-col cols="12" sm="9">{{ $t('clients.heading') }}</v-col>
                    <v-col cols="12" sm="3" class="d-flex justify-end">
                        <v-btn
                            class="minw100 mr-4"
                            color="white"
                            outlined
                            @click="showImportClients"
                        >
                            <v-icon left>fal fa-upload</v-icon>
                            {{ $t('general.import') }}
                        </v-btn>

                        <v-btn
                            class="minw100 mr-4"
                            color="white"
                            outlined
                            @click="exportClients"
                        >
                            <v-icon left>fal fa-download</v-icon>
                            {{ $t('general.export') }}
                        </v-btn>

                        <v-btn
                            class="minw100"
                            color="white"
                            outlined
                            @click="openClientForm()"
                        >
                            <v-icon left>fal fa-plus</v-icon>
                            {{ $t('general.add') }}
                        </v-btn>
                    </v-col>
                </v-row>
            </v-card-title>

            <v-card-text>
                <div class="buttons-header">
                    <v-text-field
                        v-model="filter"
                        :label="$t('general.search')"
                        append-icon="far fa-search"
                        clearable
                        clear-icon="fal fa-times"
                        single-line
                        hide-details
                        class="search"
                        @input="filterClients()"
                    />
                </div>

                <v-data-table
                    :headers="headers"
                    :items="clients"
                    :options.sync="pagination"
                    :footer-props="{ 'items-per-page-options': [10, 20, 50, 100] }"
                    :items-per-page.sync="pagination.itemsPerPage"
                    :server-items-length="pagination.total"
                    :loading="loading"
                    :mobile-breakpoint="0"
                    class="operations striped"
                >
                    <template slot="item" slot-scope="props">
                        <tr>
                            <td>{{ props.item.name }}</td>
                            <td>{{ props.item.address }}</td>
                            <td>{{ props.item.city }}</td>
                            <td>{{ props.item.country }}</td>
                            <td>{{ props.item.phone }}</td>
                            <td>{{ props.item.email }}</td>
                            <td>{{ props.item.tax_id_number || props.item.id_number }}</td>
                            <td>{{ props.item.registration_number }}</td>
                            <td>{{ props.item.vat_number }}</td>
                            <td>{{ props.item.card }}</td>
                            <td>{{ parseFloat(props.item.discount_percent) }}</td>
                            <td class="minw200">{{ props.item.comment }}</td>
                            <td align="center" class="w35 maxw60">
                                <v-menu bottom transition="slide-y-transition">
                                    <template v-slot:activator="{ on }">
                                        <v-btn v-on="on" icon>
                                            <v-icon>far fa-ellipsis-h</v-icon>
                                        </v-btn>
                                    </template>

                                    <v-list>
                                        <v-list-item :key="1" @click="openClientForm(props.item)">
                                            <v-list-item-action class="mr-4 flex justify-content-center">
                                                <v-icon>fal fa-pen</v-icon>
                                            </v-list-item-action>
                                            <v-list-item-title>{{ $t('general.edit') }}</v-list-item-title>
                                        </v-list-item>

                                        <v-divider />

                                        <v-list-item :key="2" @click="deleteClient(props.item)">
                                            <v-list-item-action class="mr-4 flex justify-content-center">
                                                <v-icon class="error--text">fal fa-times</v-icon>
                                            </v-list-item-action>
                                            <v-list-item-title class="error--text">
                                                {{ $t('general.delete') }}
                                            </v-list-item-title>
                                        </v-list-item>
                                    </v-list>
                                </v-menu>
                            </td>
                        </tr>
                    </template>
                </v-data-table>
            </v-card-text>
        </v-card>

        <!-- Import Clients -->
        <cod-dialog :show.sync="show.importClients">
            <template slot="header">
                <span>
                    {{ $t('clients.import.title') }}
                </span>
            </template>

            <div
                slot="content"
                class="pa-5"
            >
                <clients-import
                    ref="clientsImport"
                    @error="onClientsImportFailed"
                    @importStarted="importStarted"
                />
            </div>

            <template slot="footer">
                <v-btn
                    color="primary"
                    outlined
                    class="flex-1 mr-3"
                    @click="show.importClients = false"
                >
                    {{ $t('general.cancel') }}
                </v-btn>
                <v-btn
                    :disabled="importStartedBool"
                    :loading="importStartedBool"
                    color="primary"
                    depressed
                    class="flex-1"
                    @click="importClients"
                >
                    {{ $t('general.import') }}
                </v-btn>
            </template>
        </cod-dialog>
        <cod-dialog id="client-form" :show.sync="showForm">
            <!-- Header -->
            <template slot="header">
                <span>{{ !editClient ? $t('general.add_client') : $t('general.add_client') }}</span>
            </template>

            <!-- Content -->
            <div slot="content">
                <client-form
                    :admin="true"
                    :client="editClient"
                    :pricelists="pricelists"
                    @save="onClientSaved"
                />
            </div>

        </cod-dialog>
    </div>
</template>

<script>
import { loadData } from '@/views/shared/loadPageData';
import CodDialog from '@/components/CodDialog';
import ApiService from '@/services/ApiService';
import ClientForm from '@/views/shared/components/ClientForm';
import ClientsImport from '@/views/shared/components/ClientsImport';
import Client from '@/models/Client';
import _ from 'lodash';

export default {
    components: {
        CodDialog,
        ClientForm,
        ClientsImport
    },

    created() {
        loadData('/admin/load/clients').then(data => this.onDataLoaded(data));
    },

    data() {
        return {
            clients: [],
            pricelists: [],
            filter: null,
            loading: false,

            headers: [
                { text: this.$t('general.name'), value: 'name' },
                { text: this.$t('general.address'), value: 'address' },
                { text: this.$t('general.city'), value: 'city' },
                { text: this.$t('general.country'), value: 'country' },
                { text: this.$t('general.phone'), value: 'phone' },
                { text: this.$t('general.email'), value: 'email' },
                { text: this.$t('general.tax_id_number'), value: 'tax_id_number' },
                { text: this.$t('general.registration_number'), value: 'registration_number' },
                { text: this.$t('general.vat_number'), value: 'vat_number' },
                { text: this.$t('general.buyer_card_number'), value: 'card' },
                { text: this.$t('general.discount') + ' (%)', value: 'discount_percent' },
                { text: this.$t('general.comment'), sortable: false },
                { text: this.$t('general.operations'), sortable: false }
            ],

            pagination: {
                sortBy: ['name'],
                itemsPerPage: 20,
                page: 1,
                total: 0,
                sortDesc: [false]
            },

            formValid: false,

            editClient: null,
            showForm: false,
            show: {
                importClients: false
            },
            importStartedBool: false
        };
    },

    watch: {
        pagination: {
            handler() {
                this.getClients();
            },

            deep: true
        }
    },

    methods: {
        onDataLoaded(data) {
            this.pricelists = data.pricelists;
        },

        // Get paginated, filtered and sorted clients from the server
        getClients() {
            const { sortBy, sortDesc, page, itemsPerPage } = this.pagination;
            let url =
                `/admin/clients?page=${page}` +
                `&itemsPerPage=${itemsPerPage}` +
                `&sortField=${sortBy ? sortBy[0] : 'name'}` +
                `&sortOrder=${sortDesc[0] === true ? 'descending' : 'ascending'}`;

            if (this.filter) {
                url += '&filter=' + this.filter;
            }

            this.loading = true;

            ApiService.get(url).then(response => {
                this.loading = false;
                this.clients = response.data.data.map(c => new Client(c));
                this.pagination.total = response.data.total;
            });
        },

        // Filter clients
        filterClients: _.debounce(function() {
            this.getClients();
        }, 500),

        openClientForm(client = null) {
            this.editClient = client
            this.showForm = true;
        },

        onClientSaved(client) {
            this.showForm = false;
            if(!this.editClient) {
                this.clients.unshift(client.$toJson());
                this.pagination.total++;
            } else {
                this.clients.forEach((item, index, array) => {
                    if (item.id === client.id) {
                        this.$set(array, index, client.$toJson());
                    }
                });
            }
        },

        deleteClient(client) {
            this.$confirm(
                this.$t('clients.permanently_delete'),
                {
                    buttonTrueText: this.$t('general.confirm'),
                    icon: 'fal fa-times',
                    color: 'error',
                    title: this.$t('general.delete')
                },
                result => {
                    if (!result) return;

                    this.showForm = false;
                    ApiService.delete(`/admin/clients/${client.id}`, {})
                        .then(response => {
                            this.clients = this.clients.filter(item => item.id != client.id);
                            this.pagination.total--;
                            this.$snackbar(this.$t('clients.deleted'), 'success');
                        })
                        .catch(error => {
                            if (error.response.status === 400) {
                                error.response.data.errors.forEach(item => {
                                    if (item.code === 'has_dispatches') {
                                        this.$snackbar(this.$t('clients.has_dispatches'), 'error');
                                    }
                                    if(item.code === 'has_orders') {
                                        this.$snackbar(this.$t('clients.has_orders'), 'error');
                                    }
                                });
                            }
                        });
                }
            );
        },

        showImportClients(type) {
            this.show.importClients = true;
        },

        async exportClients(){
            const response = await ApiService.get(`/admin/clients/export`, {
                responseType: 'blob',
                timeout: 120000
            });

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', this.$tc('general.client', 2) + '.xlsx');
            document.body.appendChild(link);
            link.click();
        },

        async importClients() {
            if (await this.$refs.clientsImport.importFile()){
                this.show.importClients = false;
                this.importStartedBool = false;
                this.getClients();
            }
        },

        onClientsImportFailed() {
            this.$snackbar(this.$t('products.import.fail'), 'error');
        },

        importStarted(value){
            this.importStartedBool = value;
        },
    }
};
</script>

<style lang="scss" scoped>
.clients {
    .page-heading {
        padding: 12px 16px;
        color: white;
        font-size: 16px;
        word-break: break-word;
    }

    .buttons-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 20px;

        @media screen and (max-width: 599px) {
            flex-wrap: wrap;
            justify-content: flex-end;
        }

        .search {
            margin-bottom: 10px;
            padding-bottom: 5px;
            @media screen and (min-width: 600px) {
                max-width: 450px;
            }
        }
    }
}
</style>
